import { useApolloClient } from '@apollo/client/react';
import { Alert, Button, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import type { JWT } from 'aws-amplify/auth';
import type Keycloak from 'keycloak-js';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import CustomSignIn from './CustomSignIn';
import { keycloakCaller } from '../../../../libs/auth/keycloakClaims';
import { AuthState } from '../../libs';
import { parseKeycloakUser } from '../../libs/authUser';
import type { AuthUser } from '../../libs/authUser';
import { initializeKeycloak, keycloakSession, selectCognitoLogin, usesKeycloak } from '../../libs/keycloakSession';
import { clearPrivateData } from '../../libs/queryClient';

interface AuthGateProps {
  children: (_: { user: AuthUser; signOut: () => void }) => ReactNode;
}

export default function AuthGate({ children }: AuthGateProps) {
  const client = useApolloClient();
  const queryClient = useQueryClient();
  const clearPrivateCache = useCallback(() => clearPrivateData(client, queryClient), [client, queryClient]);
  const [authState, setAuthState] = useState(AuthState.loading);
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    let disposed = false;
    let keycloak: Keycloak | undefined;
    if (usesKeycloak()) {
      const restore = async () => {
        try {
          keycloak = await keycloakSession();
          const changed = () => {
            if (disposed || !usesKeycloak()) return;
            if (keycloak?.authenticated && keycloak.tokenParsed && keycloakCaller(keycloak.tokenParsed)) {
              const claims = keycloak.tokenParsed;
              const mapped = parseKeycloakUser(claims);
              setUser({ provider: 'keycloak', claims });
              setAuthState(AuthState.signedIn);
              if (mapped.kitchen.length === 0 || !mapped.permissions?.length) {
                void clearPrivateCache();
              }
            } else {
              setUser(null);
              setAuthState(AuthState.signIn);
              void clearPrivateCache();
            }
          };
          keycloak.onAuthRefreshSuccess = changed;
          keycloak.onAuthLogout = changed;
          keycloak.onTokenExpired = () => {
            if (!usesKeycloak()) return;
            void keycloak?.updateToken(30).catch(() => {
              keycloak?.clearToken();
              changed();
            });
          };
          const restored = await initializeKeycloak();
          if (disposed) return;
          if (!restored) {
            setUser(null);
            setAuthState(AuthState.signIn);
            return;
          }
          changed();
        } catch {
          if (!disposed) {
            setAuthState(AuthState.signIn);
            toast.error('Sign-in could not start. Try again, or use Google or your phone number.');
          }
        }
      };
      void restore();
    } else {
      void checkCognito();
    }
    return () => {
      disposed = true;
      if (keycloak) {
        keycloak.onAuthRefreshSuccess = undefined;
        keycloak.onAuthLogout = undefined;
        keycloak.onTokenExpired = undefined;
      }
    };
  }, [client, clearPrivateCache]);

  const checkCognito = async () => {
    let payload: JWT['payload'] | undefined;
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      payload = session.tokens?.idToken?.payload;
    } catch {
      try {
        // Stored tokens can still be valid when a forced refresh fails on a connectivity blip.
        const session = await fetchAuthSession();
        payload = session.tokens?.idToken?.payload;
      } catch {
        payload = undefined;
      }
    }
    setUser(payload ? { provider: 'cognito', claims: payload } : null);
    setAuthState(payload ? AuthState.signedIn : AuthState.signIn);
  };

  const signOut = async () => {
    const wasKeycloak = user?.provider === 'keycloak';
    void clearPrivateCache();
    setUser(null);
    setAuthState(AuthState.signIn);
    if (wasKeycloak) selectCognitoLogin();
    try {
      if (wasKeycloak) {
        const keycloak = await keycloakSession();
        await keycloak.logout({ redirectUri: `${location.origin}/` });
      } else {
        await amplifySignOut();
      }
    } catch {
      toast.error('Sign-out could not be completed. Sign in again to continue.');
    }
  };

  if (authState === AuthState.signedIn && user) {
    if (user.provider === 'keycloak') {
      const mapped = parseKeycloakUser(user.claims);
      if (mapped.kitchen.length === 0 || !mapped.permissions?.length) {
        return (
          <Stack spacing={2} sx={{ p: 3 }}>
            <Alert severity="info">
              Your account has no KDS access or kitchen yet. Ask your manager or a KDS admin to finish your setup.
            </Alert>
            <Button onClick={() => void signOut()}>Sign out</Button>
          </Stack>
        );
      }
    }
    return <>{children({ user, signOut: () => void signOut() })}</>;
  }
  return authState === AuthState.signIn ? <CustomSignIn onSignedIn={() => void checkCognito()} /> : null;
}
